import { useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@crashx/ui";
import { CrashXError } from "@crashx/shared";
import { RainAttempt } from "./rainAttempt.js";
import { chatError } from "./chatApi.js";
import { useChatStore } from "./chatStore.js";

export function RainForm({
  disabled,
  maxAmount,
  maxRecipients,
}: {
  disabled: boolean;
  maxAmount: string;
  maxRecipients: number;
}) {
  const { t } = useTranslation();
  const attempt = useRef<RainAttempt | null>(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (disabled || busy) return;
    const form = new FormData(event.currentTarget);
    const epoch = useChatStore.getState().epoch;
    setBusy(true);
    setError(null);
    setDone(false);
    try {
      const amount = form.get("amount");
      if (typeof amount !== "string") throw new CrashXError("VALIDATION");
      attempt.current ??= new RainAttempt(amount, Number(form.get("recipients")));
      setPending(true);
      const rain = await attempt.current.run();
      if (epoch !== useChatStore.getState().epoch) return;
      useChatStore.getState().notice({ kind: "rain", data: rain });
      attempt.current = null;
      setPending(false);
      setDone(true);
    } catch (cause) {
      setError(chatError(cause));
      if (cause instanceof CrashXError && cause.code !== "INTERNAL") {
        attempt.current = null;
        setPending(false);
      }
    } finally {
      setBusy(false);
    }
  }
  return (
    <details className="cx-chat-rain">
      <summary>{t("chat.rain")}</summary>
      <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-2">
        <p className="text-xs text-muted">{t("chat.rainHint", { max: maxAmount })}</p>
        <label className="cx-field">
          {t("chat.rainAmount")}
          <input
            className="cx-input"
            name="amount"
            type="number"
            min="0.01"
            max={maxAmount}
            step="0.01"
            defaultValue="1.00"
            readOnly={pending}
            required
          />
        </label>
        <label className="cx-field">
          {t("chat.recipients")}
          <input
            className="cx-input"
            name="recipients"
            type="number"
            min="1"
            max={maxRecipients}
            step="1"
            defaultValue="1"
            readOnly={pending}
            required
          />
        </label>
        <Button type="submit" disabled={busy || disabled}>
          {t(pending && !busy ? "chat.retryRain" : "chat.sendRain")}
        </Button>
        {pending && error ? <p className="text-xs">{t("chat.pendingRain")}</p> : null}
        {error ? (
          <p role="alert" className="text-danger">
            {t(error)}
          </p>
        ) : null}
        {done ? <p role="status">{t("chat.rainSent")}</p> : null}
      </form>
    </details>
  );
}
